<template>
  <v-card
      color="transparent"
      :flat="Object.values(getPaymentTypes).length === 1 || flat"
      v-if="showPayments"
  >
    <v-card-text v-if="!loading" class="payment-types" :class="cardTextClass">
      <v-radio-group
          v-if="!processing && !paymentSummary"
          v-model="paymentTypeSelected"
          hide-details
          class="mt-0"
      >
        <template v-if="paidCouponComponent && !processing && !loading && !onBasket">
          <v-checkbox
              v-model="isVisiblePaidCoupon"
              :label="$language.translate('PAID_COUPON_LABEL')"
          />
          <component
              :is="paidCouponComponent"
              v-model="isVisiblePaidCoupon"
              @success="$emit('success', $event)"
              @hide-payments="onHidePayments"
              @paid-coupon-used="onPaidCouponChange"
          />
        </template>
        <template v-for="(paymentType, paymentTypeIndexname) in getPaymentTypes">
          <div :key="paymentTypeIndexname" class="mb-1">
            <v-radio
                v-if="Object.values(getPaymentTypes).length > 1 && isAgregator(paymentType) === false"
                :label="getPaymentTypeLabel(paymentType)"
                :value="paymentTypeIndexname"
            />

            <template
                v-if="paymentTypeSelected === paymentTypeIndexname || isAgregator(paymentType) === true"
            >
              <component
                  :is="paymentType.vueJsComponent"
                  :shipping-address="shippingAddress"
                  :key="paymentTypeIndexname"
                  :button-large="buttonLarge"
                  :button-color="buttonColor"
                  :button-round="buttonRound"
                  :onBasket="onBasket"
                  @cancel="$emit('cancel')"
                  @success="$emit('success', $event)"
                  @received="$emit('received', $event)"
                  @editAddress="$emit('editAddress', $event)"
                  v-model="paymentTypeSelected"
              />
            </template>
          </div>
        </template>
      </v-radio-group>

      <template v-else-if="getPaymentTypes[paymentTypeSelected]">
        <v-layout row wrap justify-center v-if="!paymentSummary">
          <v-flex py-4 shrink>
            <v-progress-circular indeterminate color="primary"/>
          </v-flex>
        </v-layout>

        <component
            :is="getPaymentTypes[paymentTypeSelected].vueJsComponent"
            :noButton="true"
            :button-large="buttonLarge"
            :button-color="buttonColor"
            :button-round="buttonRound"
            :onBasket="onBasket"
            @cancel="$emit('cancel')"
            @success="$emit('success', $event)"
            @payment-success="$emit('success', $event)"
            @received="$emit('received', $event)"
        />
      </template>
    </v-card-text>

    <v-layout v-else row wrap justify-center>
      <v-flex py-4 shrink>
        <v-progress-circular indeterminate color="primary"/>
      </v-flex>
    </v-layout>

    <v-dialog v-model="displayErrorModal" width="550">
      <v-card>
        <v-card-text>
          {{'An error occurred on payment. Here are the details:' | translation}}
          <br />
          <span v-html="$language.translate(error)" />
        </v-card-text>

        <v-card-actions>
          <v-btn @click="displayErrorModal = false">{{'Close' | translation}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<style>
.payment-types .v-input--selection-controls .v-input__control {
  width: 100%;
}

.payment-types .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.payment-types .v-input--selection-controls:not(.v-input--hide-details) .v-input__slot,
.payment-types .v-input--radio-group--column .v-radio:not(:last-child):not(:only-child) {
  margin-bottom: 0;
}

.payment-types .v-radio .v-label,
.payment-types .v-input--checkbox .v-label
{
  color: var(--v-black) !important;
}
</style>

<script>
import Vue from 'vue';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import { PaymentTypeAgregator } from './api/payment.type.agregator.nst';
import { PaymentTypeCountryDependentFee } from './api/payment.type.country.dependent.fee.nst';

const moduleName = 'Payment';

export default {
  name: moduleName,
  props: {
    processing: Boolean,
    onBasket: Boolean,
    buttonLarge: Boolean,
    buttonRound: Boolean,
    buttonColor: {
      type: String,
      default: 'primary'
    },
    flat: Boolean,
    cardTextClass: String,
  },
  data() {
    return {
      isVisiblePaidCoupon: false,
      hidePayments: false,
      filterPartialPayments: false,
    };
  },
  beforeDestroy() {
    this.paymentTypeSelected = null;
  },
  computed: {
    ...mapState(moduleName, [
      'paymentTypes',
      'paymentSummary',
      'paymentTypesConfiguration',
      'paymentTypeConfigurationLoaded',
      'error',
      'loading',
    ]),
    ...mapGetters(moduleName, ['getPaymentTypeSelected']),
    shippingAddress() {
      return this.$order.getShippingAddress();
    },
    getPaymentTypes() {
      if (this.hidePayments) {
        return {};
      }
      if (!this.processing && this.paymentTypeConfigurationLoaded) {
        const countryShortName = this.shippingAddress
            ? this.shippingAddress.country
            : this.$country.getCountry();
        const allowedPaymentTypes = [];
        const paymentTypesIndexnames = Object.keys(this.paymentTypes);

        for (const paymentTypeIndexname of paymentTypesIndexnames) {
          const paymentType = this.paymentTypes[paymentTypeIndexname];
          if (
              this.filterPartialPayments &&
              !paymentType.supportPartialPayment
          ) {
            continue;
          }
          if (this.paymentTypesConfiguration.hasOwnProperty(paymentTypeIndexname)) {
            const paymentTypeConfiguration = this.paymentTypesConfiguration[paymentTypeIndexname];
            if (paymentTypeConfiguration.isActive) {
              if (
                  (!paymentTypeConfiguration.countries.length ||
                      paymentTypeConfiguration.countries.includes(countryShortName)) &&
                  (!this.onBasket || paymentType.useOnBasket)
              ) {
                allowedPaymentTypes.push(paymentType);
              }
            }
          } else if (
              this.paymentTypesConfiguration[paymentTypeIndexname] &&
              (!this.onBasket || paymentType.useOnBasket)
          ) {
            allowedPaymentTypes.push(paymentType);
          }
        }

        if (allowedPaymentTypes.length === 1) {
          this.paymentTypeSelected = allowedPaymentTypes[0].indexname;
        }

        const sortIndexnames = Object.keys(this.paymentTypesConfiguration);
        allowedPaymentTypes.sort((a, b) => {
          return sortIndexnames.indexOf(a.indexname) - sortIndexnames.indexOf(b.indexname);
        });

        let result = {};
        allowedPaymentTypes.forEach(paymentType => result[paymentType.indexname] = paymentType);

        this.$order
            .getExcludedPaymentTypes()
            .forEach(excludedPaymentType => delete result[excludedPaymentType]);

        return result;
      } else if (this.processing) {
        return this.paymentTypes;
      }
      return {};
    },
    paymentTypeSelected: {
      get() {
        return this.getPaymentTypeSelected;
      },
      set(value) {
        this.setPaymentTypeSelected(value);
      },
    },
    displayErrorModal: {
      get() {
        return Boolean(this.error);
      },
      set() {
        this.setError(null);
      }
    },
    country() {
      return this.$order.getShippingAddress() ? this.$order.getShippingAddress().country : this.$country.getCountry();
    },
    basketTotal() {
      return this.$basket.getBasketTotal();
    },
    pickupShipping() {
      return this.$order.isShippingPickup();
    },
    paidCouponComponent() {
      return Vue.options.components['paid-coupon'];
    },
    showPayments() {
      let result = false;
      const totalActivePaymentTypes = Object.keys(this.getPaymentTypes).length;
      if (
          this.hidePayments || this.filterPartialPayments || totalActivePaymentTypes > 0
      ) {
        result = true;
      }
      return result;
    }
  },
  methods: {
    ...mapMutations(moduleName, ['setPaymentTypeSelected', 'setError']),
    ...mapActions(moduleName, ['loadAllowedPayments']),
    getPaymentTypeLabel(paymentType) {
      const labelParts = new Set();
      const suffix = this.$order.getDeliveryCountry();
      labelParts.add(paymentType.label);
      if (paymentType instanceof PaymentTypeCountryDependentFee) {
        labelParts.add(`-${suffix}`);
      }
      return this.$language.translate([...labelParts].join(''));
    },
    isAgregator(paymentType) {
      return paymentType instanceof PaymentTypeAgregator;
    },
    loadPayments() {
      this.loadAllowedPayments({
        country: this.country,
        basketTotal: this.$basket.getBasketTotal(),
        differentShippingAddress: this.$order.isShippingAddressDifferent(),
        pickupShipping: this.$order.isShippingPickup(),
      });
    },
    onPaidCouponChange(paidCouponUsed) {
      this.filterPartialPayments = paidCouponUsed;
    },
    onHidePayments(hidePayments) {
      this.hidePayments = hidePayments;
    },
  },
  watch: {
    $route: {
      handler(route) {
        if (route.query.payment) {
          this.paymentTypeSelected = route.query.payment;
          this.$router.replace({
            ...route,
            query: {
              ...route.query,
              payment: undefined,
            },
          });
        }
      },
      immediate: true,
    },
    country() {
      this.loadPayments();
    },
    basketTotal: {
      immediate: true,
      handler() {
        this.loadPayments();
      },
    },
    pickupShipping() {
      this.loadPayments();
    },
  },
};
</script>
